// src/components/fulfillment/FulfillmentFormsPanel.tsx
//
// Fulfillment → Forms mode (org custom forms slice F2). Lists draft/saved
// form instances and opens the fill + PDF flow.

import { useCallback, useEffect, useState } from "react";
import { listOrgFormTemplates } from "@/lib/opportunity/orgFormTemplateClient";
import {
  createOrgFormInstance,
  listOrgFormInstances,
} from "@/lib/opportunity/orgFormInstanceClient";
import type { OrgFormInstanceListItem } from "@/lib/opportunity/orgFormInstanceTypes";
import type { OrgFormTemplate } from "@/lib/opportunity/orgFormTemplateTypes";
import { OrgFormFillModal } from "./OrgFormFillModal";
import { FulfillmentSignMonitorSection } from "./FulfillmentSignMonitorSection";
import { FulfillmentFormsRecentBlock } from "./FulfillmentFormsRecentBlock";

export function FulfillmentFormsPanel() {
  const [templates, setTemplates] = useState<OrgFormTemplate[]>([]);
  const [instances, setInstances] = useState<OrgFormInstanceListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [selectedTemplateId, setSelectedTemplateId] = useState("");
  const [creating, setCreating] = useState(false);
  const [fillInstanceId, setFillInstanceId] = useState<string | null>(null);
  const [reloadTick, setReloadTick] = useState(0);
  const [recentActionError, setRecentActionError] = useState<string | null>(null);

  const reload = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    const [templatesR, instancesR] = await Promise.all([
      listOrgFormTemplates(),
      listOrgFormInstances(),
    ]);
    setLoading(false);
    if (!templatesR.ok) {
      setLoadError(templatesR.error);
      return;
    }
    if (!instancesR.ok) {
      setLoadError(instancesR.error);
      return;
    }
    setTemplates(templatesR.data.templates);
    setInstances(instancesR.data.instances);
  }, []);

  useEffect(() => {
    void reload();
  }, [reload, reloadTick]);

  useEffect(() => {
    if (templates.length === 0) return;
    setSelectedTemplateId((prev) => prev || templates[0]?.id || "");
  }, [templates]);

  async function handleCreate() {
    if (!selectedTemplateId) return;
    setCreating(true);
    const r = await createOrgFormInstance({
      form_template_id: selectedTemplateId,
      source_type: "standalone",
    });
    setCreating(false);
    if (!r.ok) {
      setLoadError(r.error);
      return;
    }
    setFillInstanceId(r.data.instance.id);
    setReloadTick((t) => t + 1);
  }

  if (loading) {
    return <p className="text-sm text-gray-500 dark:text-zinc-400">Loading forms…</p>;
  }

  if (templates.length === 0) {
    return (
      <div className="mx-auto max-w-lg rounded-xl border border-dashed border-gray-300 bg-gray-50 px-6 py-10 text-center dark:border-zinc-600 dark:bg-zinc-900/40">
        <h3 className="text-base font-semibold text-gray-900 dark:text-zinc-100">No org forms yet</h3>
        <p className="mt-2 text-sm text-gray-600 dark:text-zinc-300">
          Ask a manager to create form templates under Manager → Documents Editor → Documents.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-5">
      <header className="space-y-1">
        <h2 className="text-lg font-semibold text-gray-900 dark:text-zinc-100">Forms</h2>
        <p className="text-sm text-gray-600 dark:text-zinc-300">
          Fill org paperwork, preview the PDF, and save it to storage.
        </p>
      </header>

      {loadError ? (
        <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800 dark:border-red-800 dark:bg-red-950/40 dark:text-red-100">
          {loadError}
        </div>
      ) : null}

      <div className="flex flex-wrap items-end gap-2 rounded-xl border border-gray-200 bg-white p-4 dark:border-zinc-700 dark:bg-zinc-900">
        <label className="min-w-[12rem] flex-1 text-xs">
          <span className="text-gray-500">New form template</span>
          <select
            value={selectedTemplateId}
            onChange={(e) => setSelectedTemplateId(e.target.value)}
            className="mt-1 w-full rounded-lg border border-gray-200 px-2 py-1.5 text-sm dark:border-zinc-700 dark:bg-zinc-800"
          >
            {templates.map((t) => (
              <option key={t.id} value={t.id}>
                {t.display_name}
              </option>
            ))}
          </select>
        </label>
        <button
          type="button"
          disabled={creating || !selectedTemplateId}
          onClick={() => void handleCreate()}
          className="rounded-lg bg-indigo-600 px-4 py-2 text-sm font-medium text-white disabled:opacity-50"
        >
          {creating ? "Starting…" : "Start form"}
        </button>
      </div>

      <div className="rounded-xl border border-gray-200 bg-white dark:border-zinc-700 dark:bg-zinc-900">
        <div className="border-b border-gray-100 px-4 py-2 text-xs font-medium uppercase tracking-wide text-gray-500 dark:border-zinc-800">
          Recent forms
        </div>
        <FulfillmentFormsRecentBlock
          instances={instances}
          onOpenInstance={setFillInstanceId}
          onInstancesChanged={() => setReloadTick((t) => t + 1)}
          actionError={recentActionError}
          onActionError={setRecentActionError}
        />
      </div>

      {fillInstanceId ? (
        <OrgFormFillModal
          instanceId={fillInstanceId}
          onClose={() => setFillInstanceId(null)}
          onSaved={() => setReloadTick((t) => t + 1)}
        />
      ) : null}

      <FulfillmentSignMonitorSection />
    </div>
  );
}
